import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import TweenMax from 'TweenMax';
import 'ScrollToPlugin';
import { withFormik } from 'formik';
import { inject, observer } from 'mobx-react';
import * as yup from 'yup';

import { crossScreenMarginBottom, crossScreenMarginLeft, crossScreenWidth, link } from '../../../../utils/styles';
import steps from './lib/steps';
import BotMessage from './components/BotMessage';
import api from '../../../../utils/api';
import iconOk from './img/icon-ok.png';

const STATUS_SUBMITTING = 'submitting';
const STATUS_SUCCESS = 'success';
const STATUS_ERROR = 'error';

const BotFinalMessage = styled(BotMessage)`
  opacity: 0;
  visibility: hidden;
  margin-bottom: 0;
`;

const StyledLink = styled.span`
  ${link};
`;

const StepContainer = styled.div`
  ${crossScreenMarginBottom(116)};
`;

const Icon = styled.img`
  position: relative;
  bottom: -1px;
  height: auto;
  ${crossScreenMarginLeft(4)};
  ${crossScreenWidth(15)};
`;

class BriefBot extends React.PureComponent {
  static propTypes = {
    values: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    submitForm: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    status: PropTypes.string,
    errors: PropTypes.object.isRequired,
    validateForm: PropTypes.func.isRequired,
  };

  steps = [...steps];

  state = {
    steps: [],
    currentIndex: -1,
  };

  componentDidMount() {
    this.nextStep();
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.status !== this.props.status &&
      (nextProps.status === STATUS_SUCCESS || nextProps.status === STATUS_ERROR)
    ) {
      this.showSuccessMessage();
    }
  }

  showSuccessMessage() {
    TweenMax.fromTo(this.successMessageNode, 0.35, { autoAlpha: 0, y: 50 }, { autoAlpha: 1, y: 0 });
  }

  handleInputSubmit() {
    const schema = yup.object().shape(
      this.state.steps.reduce((acc, curr) => {
        return {
          ...acc,
          [curr.valueKey]: curr.validation,
        };
      }, {})
    );

    schema.isValid(this.props.values).then(isValid => {
      if (isValid) {
        this.nextStep();
      }
    });
  }

  nextStep() {
    if (this.steps.length === 0) {
      this.props.submitForm();
      return;
    }

    const nextStep = this.steps.shift();

    this.props.setFieldValue(nextStep.valueKey, '', false);

    this.setState(
      state => ({ steps: [...state.steps, nextStep], currentIndex: state.currentIndex + 1 }),
      () => {
        const activeStepNode = this.containerNode.querySelector('[data-active="true"]');
        const questionNode = activeStepNode.querySelector('[data-question]');
        const answerNode = activeStepNode.querySelector('[data-answer]');

        if (this.state.currentIndex > 0) {
          questionNode && this.animateQuestionShowing(questionNode);
          answerNode && this.animateAnswerShowing(answerNode);
          TweenMax.to(window, 1, {
            scrollTo: { y: activeStepNode, offsetY: 120 },
            ease: Power1.easeOut,
          });
        }
      }
    );
  }

  animateQuestionShowing(el) {
    TweenMax.fromTo(el, 0.35, { autoAlpha: 0, y: 50 }, { autoAlpha: 1, y: 0 });
  }

  animateAnswerShowing(el) {
    TweenMax.fromTo(
      el,
      0.35,
      { autoAlpha: 0, y: 50 },
      {
        autoAlpha: 1,
        y: 0,
        delay: 0.5,
        onComplete: () => {
          const inputText = el.querySelector('input[type="text"]');
          inputText && inputText.focus();
        },
      }
    );
  }

  renderSuccessMessage = () => (
    <React.Fragment>
      Прекрасно <Icon src={iconOk} />
      <br />
      Мы ответим вам совсем скоро.
    </React.Fragment>
  );

  renderErrorMessage = () => (
    <React.Fragment>
      Извините, <br />
      произошла ошибка, <StyledLink onClick={() => this.props.submitForm()}>повторим?</StyledLink>
    </React.Fragment>
  );

  render() {
    const { values, errors, handleChange, setFieldValue } = this.props;

    return (
      <div ref={node => (this.containerNode = node)}>
        <form method="POST" onSubmit={e => e.preventDefault()}>
          {this.state.steps.map((step, index) => {
            const {
              botMessage: { component: BotMessage },
              userMessage: { component: UserMessage },
              valueKey,
            } = step;

            return (
              <StepContainer key={index} data-active={index === this.state.steps.length - 1}>
                {BotMessage && <BotMessage index={index} userName={values['f_name']} />}
                {UserMessage && (
                  <UserMessage
                    onSubmit={() => this.handleInputSubmit()}
                    name={valueKey}
                    value={values[valueKey]}
                    onChange={handleChange}
                    setFieldValue={setFieldValue}
                    _error={String(errors[valueKey])}
                    disabled={this.props.status === STATUS_SUCCESS}
                  />
                )}
              </StepContainer>
            );
          })}
        </form>

        <BotFinalMessage messageRef={node => (this.successMessageNode = node)}>
          {this.props.status === STATUS_SUCCESS ? this.renderSuccessMessage() : this.renderErrorMessage()}
        </BotFinalMessage>
      </div>
    );
  }
}

const BriefBotForm = withFormik({
  validate: values => {
    const errors = {};

    delete values.progressStore;

    Object.keys(values).forEach(key => {
      const isValid = steps.filter(s => s.valueKey === key)[0].validation.isValidSync(values[key]);

      if (!isValid) {
        errors[key] = true;
      }
    });

    return errors;
  },
  handleSubmit: (values, { setStatus, props: { progressStore } }) => {
    setStatus(STATUS_SUBMITTING);
    progressStore.turnOn();
    values = {
      ...values,
      catalogue: 4,
      cc: 55,
      sub: 55,
      posting: 1,
      f_Checked: 1,
      isNaked: 1,
      nc_token: '584c57e113e76778b0700646510b9a97',
    };
    api
      .sendBriefForm(values)
      .then(data => {
        data.json().then(data => console.log(data));
        setStatus(STATUS_SUCCESS);
        progressStore.turnOff();
      })
      .catch(err => {
        console.error(err);
        setStatus(STATUS_ERROR);
        progressStore.turnOff();
      });
  },
})(BriefBot);

export default inject(stores => ({ progressStore: stores.store.progress }))(observer(BriefBotForm));



// WEBPACK FOOTER //
// ./src/scenes/Brief/components/BriefBot/index.js